<template>
  <div class="searchable-select">
    <div class="position-relative">
      <!-- Input de búsqueda -->
      <input
        ref="searchInput"
        type="text"
        class="form-control"
        :placeholder="placeholder"
        v-model="searchQuery"
        @focus="showDropdown = true"
        @blur="handleBlur"
        @input="filterItems"
        :disabled="disabled"
      />

      <!-- Dropdown con resultados -->
      <div
        v-show="showDropdown && filteredItems.length > 0"
        class="dropdown-menu show w-100"
        style="max-height: 250px; overflow-y: auto; position: absolute; z-index: 1000;"
      >
        <button
          type="button"
          class="dropdown-item"
          v-for="item in filteredItems"
          :key="getItemValue(item)"
          @mousedown.prevent="selectItem(item)"
          :class="{ 'active': getItemValue(item) === modelValue }"
        >
          {{ getItemLabel(item) }}
        </button>
      </div>

      <!-- Mensaje cuando no hay resultados -->
      <div
        v-show="showDropdown && filteredItems.length === 0 && searchQuery"
        class="dropdown-menu show w-100"
        style="position: absolute; z-index: 1000;"
      >
        <div class="dropdown-item text-muted" disabled>
          No se encontraron resultados
        </div>
      </div>
    </div>

    <!-- Botón para limpiar selección -->
    <button
      v-if="modelValue && clearable"
      type="button"
      class="btn btn-sm btn-outline-secondary mt-1"
      @click="clearSelection"
      :disabled="disabled"
    >
      <i class="bi bi-x"></i> Limpiar selección
    </button>

    <!-- Mostrar elemento seleccionado -->
    <div v-if="modelValue && showSelected" class="mt-2 p-2 bg-light rounded">
      <small class="text-muted">Seleccionado:</small>
      <div class="fw-semibold">{{ selectedItemLabel }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchableSelect',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    itemLabel: {
      type: String,
      default: 'name'
    },
    placeholder: {
      type: String,
      default: 'Buscar...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    showSelected: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      searchQuery: '',
      showDropdown: false,
      filteredItems: []
    };
  },
  computed: {
    selectedItemLabel() {
      if (!this.modelValue) return '';
      const selected = this.items.find(item => this.getItemValue(item) === this.modelValue);
      return selected ? this.getItemLabel(selected) : '';
    }
  },
  watch: {
    items: {
      immediate: true,
      handler() {
        this.filterItems();
      }
    },
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.searchQuery) {
          const selected = this.items.find(item => this.getItemValue(item) === newVal);
          if (selected) {
            this.searchQuery = this.getItemLabel(selected);
          }
        } else if (!newVal) {
          this.searchQuery = '';
        }
      }
    }
  },
  methods: {
    getItemValue(item) {
      return typeof item === 'object' ? item[this.itemValue] : item;
    },
    getItemLabel(item) {
      return typeof item === 'object' ? item[this.itemLabel] : item;
    },
    filterItems() {
      const query = this.searchQuery.toLowerCase().trim();

      if (!query) {
        this.filteredItems = this.items.slice(0, 50); // Mostrar solo las primeras 50 opciones
      } else {
        this.filteredItems = this.items.filter(item => {
          const label = this.getItemLabel(item).toLowerCase();
          return label.includes(query);
        }).slice(0, 50);
      }
    },
    selectItem(item) {
      const value = this.getItemValue(item);
      this.searchQuery = this.getItemLabel(item);
      this.$emit('update:modelValue', value);
      this.showDropdown = false;
    },
    clearSelection() {
      this.searchQuery = '';
      this.$emit('update:modelValue', '');
      this.filterItems();
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    handleBlur() {
      setTimeout(() => {
        this.showDropdown = false;
      }, 200);
    }
  },
  mounted() {
    this.filterItems();
  }
};
</script>

<style scoped>
.searchable-select {
  position: relative;
}

.dropdown-menu {
  display: block;
}

.dropdown-item {
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.active {
  background-color: #0d6efd;
  color: white;
}
</style>
